/* eslint-disable */
import {useEffect, useState} from 'react';
import {Modal, Pressable, StyleSheet, Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import OnboardingButton from './onboarding/OnboardingButton';
import {getOnboardingConfig} from '../services/onboarding-service';
import AppIcon from './onboarding/AppIcon';
import {colors, fonts, radii, spacing, typography} from '../config/theme';

// All fuel types. `id` matches the API fuel ids and the icon names in ICON_SHAPES.
export const FUEL_TYPES = [
  {id: 'benzine', title: 'Benzine', sub: 'Petrol / Gas'},
  {id: 'diesel', title: 'Diesel', sub: 'Heavy Duty'},
  {id: 'electric', title: 'Electric', sub: 'EV & Hybrid'},
  {id: 'lpg', title: 'LPG / Gas', sub: 'Autogas'},
];

// Same fuel list as onboarding (from the API), with the built-in list as
// fallback. Loads while `enabled` is true.
export const useFuelTypeOptions = (enabled = true) => {
  const {i18n} = useTranslation();
  const [options, setOptions] = useState(FUEL_TYPES);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let active = true;
    const lang = (i18n.language || 'en').split('-')[0];
    getOnboardingConfig(lang)
      .then(data => {
        const list = data?.fuelType?.options;
        if (active && list?.length) {
          setOptions(
            list.map(o => ({id: o.id, title: o.label, sub: o.sublabel})),
          );
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [enabled, i18n.language]);

  return options;
};

// Bottom sheet to change the fuel type picked during onboarding.
//   value:    current fuel id (or null)
//   onSave:   async (fuelId) => void   (should throw on failure)
// Pass onSelect instead of onSave to pick and close on tap, with no Save
// button (title/subtitle then describe that choice).
const FuelTypeModal = ({
  visible,
  onClose,
  value,
  onSave,
  onSelect,
  loading,
  title,
  subtitle,
}) => {
  const {t} = useTranslation();
  const [selected, setSelected] = useState(value);
  const options = useFuelTypeOptions(visible);

  // Start from the saved fuel every time the sheet opens.
  useEffect(() => {
    if (visible) {
      setSelected(value);
    }
  }, [visible, value]);

  const changed = !!selected && selected !== value;

  const handleSave = async () => {
    try {
      await onSave(selected);
      onClose();
    } catch (e) {
      // the caller shows the error toast; keep the sheet open
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose} />
      <View style={styles.sheet}>
        <View style={styles.handle} />
        <Text style={styles.title}>
          {title || t('root.ProfileList.fuelModalTitle', 'Vehicle & Fuel Type')}
        </Text>
        <Text style={styles.subtitle}>
          {subtitle ||
            t(
              'root.ProfileList.fuelModalSubtitle',
              'Choose the fuel your vehicle uses. We use it for price alerts and savings.',
            )}
        </Text>

        <View style={styles.list} accessibilityRole="radiogroup">
          {options.map(fuel => {
            const active = selected === fuel.id;
            return (
              <Pressable
                key={fuel.id}
                onPress={() => {
                  if (onSelect) {
                    onSelect(fuel.id);
                    onClose();
                  } else {
                    setSelected(fuel.id);
                  }
                }}
                accessibilityRole="radio"
                accessibilityState={{selected: active}}
                style={[styles.option, active && styles.optionActive]}>
                <View style={[styles.iconWrap, active && styles.iconWrapActive]}>
                  <AppIcon
                    name={fuel.id}
                    size={22}
                    color={active ? colors.onPrimary : colors.primary}
                  />
                </View>
                <View style={styles.optionText}>
                  <Text style={styles.optionTitle}>{fuel.title}</Text>
                  <Text style={styles.optionSub}>{fuel.sub}</Text>
                </View>
                <View style={[styles.radio, active && styles.radioActive]}>
                  {active && (
                    <AppIcon name="check" size={12} color={colors.onPrimary} />
                  )}
                </View>
              </Pressable>
            );
          })}
        </View>

        {!onSelect && (
        <OnboardingButton
          title={
            loading
              ? t('root.ProfileList.fuelSaving', 'SAVING…')
              : t('root.ProfileList.fuelSave', 'SAVE')
          }
          onPress={handleSave}
          disabled={!changed || loading}
        />
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  sheet: {
    backgroundColor: colors.background,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xl,
    gap: spacing.md,
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
    marginBottom: spacing.xs,
  },
  title: {
    fontFamily: fonts.bold,
    fontSize: 18,
    lineHeight: 24,
    color: colors.textStrong,
  },
  subtitle: {
    ...typography.bodyMd,
    color: colors.textMuted,
  },
  list: {
    gap: spacing.sm,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.card,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  optionActive: {
    borderColor: colors.primary,
    backgroundColor: colors.surfaceMuted,
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceMuted,
  },
  iconWrapActive: {
    backgroundColor: colors.primary,
  },
  optionText: {
    flex: 1,
  },
  optionTitle: {
    fontFamily: fonts.bold,
    fontSize: 14,
    lineHeight: 20,
    color: colors.textStrong,
  },
  optionSub: {
    fontFamily: fonts.regular,
    fontSize: 12,
    lineHeight: 16,
    color: colors.textMuted,
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1.5,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
});

export default FuelTypeModal;